import type { FindingContext, SourceType, ValidationIssue } from "../../types";
import { SOURCE_TYPE_LABELS } from "../../types";

// Renders the actionable "card" attached to a finding: which source record
// it traces to, what that record actually shows, and exactly what the
// generated text got wrong — so a reviewer can accept or dismiss in a few
// seconds without opening the record. Shows only the fields present; renders
// nothing when the finding carries no context (older stored blobs).
//
// Used in all three finding surfaces: the inline marker popover, the
// ValidationBadge dropdown, and the findings summary bar.
export default function FindingContextCard({
  finding,
}: {
  finding: ValidationIssue;
}) {
  const ctx = finding.context;
  if (!ctx || !hasContent(ctx)) return null;

  const typeLabel =
    ctx.record_type && ctx.record_type in SOURCE_TYPE_LABELS
      ? SOURCE_TYPE_LABELS[ctx.record_type as SourceType]
      : ctx.record_type;

  const hasRecordMeta =
    ctx.record_date ||
    ctx.record_subject ||
    typeLabel ||
    ctx.record_from ||
    ctx.record_to;

  return (
    <div className="finding-ctx">
      {hasRecordMeta && (
        <div className="finding-ctx-record">
          <div className="finding-ctx-meta">
            {ctx.record_date && (
              <span className="finding-ctx-date">{ctx.record_date}</span>
            )}
            {typeLabel && (
              <span className="finding-ctx-chip">{typeLabel}</span>
            )}
            {(ctx.record_from || ctx.record_to) && (
              <span className="finding-ctx-parties">
                {ctx.record_from ?? "?"} → {ctx.record_to ?? "?"}
              </span>
            )}
          </div>
          {ctx.record_subject && (
            <div className="finding-ctx-subject">{ctx.record_subject}</div>
          )}
          {ctx.record_summary && (
            <div className="finding-ctx-summary">
              <span className="finding-ctx-label">This record discusses:</span>{" "}
              {ctx.record_summary}
            </div>
          )}
          {ctx.record_excerpt && (
            <blockquote className="finding-ctx-excerpt">
              {ctx.record_excerpt}
            </blockquote>
          )}
          {!!ctx.attachment_count && ctx.attachment_count > 0 && (
            <div className="finding-ctx-attach">
              📎 {ctx.attachment_count} attachment
              {ctx.attachment_count === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}

      {(ctx.ai_stated ||
        ctx.record_shows ||
        ctx.record_quote ||
        ctx.generated_sentence) && (
        <dl className="finding-ctx-diff">
          {ctx.ai_stated && (
            <Row label="AI stated" value={ctx.ai_stated} tone="ai" />
          )}
          {ctx.generated_sentence &&
            ctx.generated_sentence !== ctx.ai_stated && (
              <Row
                label="In the text"
                value={`“${ctx.generated_sentence}”`}
                tone="ai"
              />
            )}
          {ctx.record_shows && (
            <Row label="Record shows" value={ctx.record_shows} tone="rec" />
          )}
          {ctx.record_quote && (
            <Row
              label="Record says"
              value={`“${ctx.record_quote}”`}
              tone="rec"
            />
          )}
        </dl>
      )}

      {ctx.body_truncated && (
        <div className="finding-ctx-note">
          ⚠ The source record body was truncated — this could not be fully
          verified.
        </div>
      )}
      {ctx.no_citation && (
        <div className="finding-ctx-note">No source citation provided.</div>
      )}
      {ctx.citation_ref && (
        <div className="finding-ctx-diff">
          <Row label="Citation" value={ctx.citation_ref} tone="ai" />
          {ctx.citation_target && (
            <Row label="Points to" value={ctx.citation_target} tone="rec" />
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "ai" | "rec";
}) {
  return (
    <div className={`finding-ctx-row tone-${tone}`}>
      <dt className="finding-ctx-label">{label}</dt>
      <dd className="finding-ctx-value">{value}</dd>
    </div>
  );
}

function hasContent(ctx: FindingContext): boolean {
  for (const v of Object.values(ctx)) {
    if (v !== undefined && v !== "" && v !== false) return true;
  }
  return false;
}
